// User-management API client — invite + edit role + edit regions + detail.
// Mirrors backend/src/routes/users.routes.ts. The backend's error responses
// carry a stable `code` field that callers branch on for tailored UX (the
// 409/403 codes from BON-53 link-validation and BON-54 edit-role guards).

import type { InviteConnection, ProvisioningStatus } from '@bonsai/shared';

import { apiFetch } from '../lib/apiFetch';

export type RoleKey =
  | 'admin'
  | 'regional-director'
  | 'marketing'
  | 'instructor'
  | 'finance'
  | 'csr';

// ── Discriminated error shape ────────────────────────────────────────────

/**
 * Codes the backend can return on the user-management surface. The route
 * always echoes the code into the response body so the UI can render
 * tailored messages without parsing the human-readable `message`.
 */
export type UserMgmtErrorCode =
  // BON-53 invite-side validation
  | 'LINK_NOT_FOUND'
  | 'LINK_ALREADY_LINKED'
  | 'LINK_EMAIL_MISMATCH'
  // BON-54 RD-scope (shared by invite + edit)
  | 'RD_FORBIDDEN_ROLE'
  | 'RD_OUT_OF_SCOPE_REGION'
  // BON-54 edit-side guards
  | 'TARGET_NOT_FOUND'
  | 'TARGET_HAS_NO_AUTH0'
  | 'TARGET_INACTIVE'
  | 'TARGET_ALREADY_ACTIVE'
  | 'SELF_TARGET'
  | 'LAST_ADMIN_GUARD'
  | 'RD_TARGET_OUT_OF_SCOPE'
  | 'TARGET_OUTRANKS_CALLER'
  | 'REGION_SCOPED_ROLE_NEEDS_REGION'
  | 'LINK_NOT_IMPORTED'
  | 'NOT_INVITED';

/** The login already on an invite's email, sent with `EMAIL_HAS_AUTH0_ACCOUNT`. */
export interface ExistingAccountDetails {
  /** Role keys, or the Auth0 role name for a role outside the taxonomy. */
  existingRoles: string[];
  /** How the login signs in today; linking keeps it. */
  signInMethod: InviteConnection;
}

/** Typed error surfaced from the API client. Callers branch on `code` for
 *  tailored UI; the message is the backend's human-readable explanation. */
export class UserMgmtError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: UserMgmtErrorCode | string | undefined,
    message: string,
    public readonly details?: ExistingAccountDetails,
  ) {
    super(message);
    this.name = 'UserMgmtError';
  }
}

interface OkResponse<T> {
  status: 'ok';
  /** Endpoint-specific payload — the keys differ per route (see below). */
  invite?: T;
  user?: T;
  result?: T;
  preflight?: T;
}

interface ErrorResponse {
  status: 'error';
  message?: string;
  code?: string;
  details?: ExistingAccountDetails;
}

/** Sends a request and throws `UserMgmtError` on any non-2xx response. */
async function request<TPayload, TBody extends { status: 'ok' }>(
  method: 'POST' | 'PATCH' | 'GET',
  url: string,
  body?: TPayload,
): Promise<TBody> {
  const response = await apiFetch(url, {
    method,
    ...(body !== undefined && { body: JSON.stringify(body) }),
  });
  // 204 No Content (rare on this surface; defensive). Treat as ok.
  if (response.status === 204) {
    return { status: 'ok' } as TBody;
  }
  const json = (await response.json().catch(() => ({}))) as TBody | ErrorResponse;
  if (!response.ok) {
    const err = json as ErrorResponse;
    throw new UserMgmtError(
      response.status,
      err.code,
      err.message ?? response.statusText,
      err.details,
    );
  }
  return json as TBody;
}

/** Sends a request whose success payload sits under a per-route key. */
function send<TPayload, TResponse>(
  method: 'POST' | 'PATCH' | 'GET',
  url: string,
  body?: TPayload,
): Promise<OkResponse<TResponse>> {
  return request<TPayload, OkResponse<TResponse>>(method, url, body);
}

// ── Invite ───────────────────────────────────────────────────────────────

export interface InviteUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  /** One or more role keys. Multi-role at invite time per spec. */
  roleKeys: RoleKey[];
  /** Region UUIDs. Required when any selected role is region-scoped. */
  regionIds: string[];
  /** An existing imported row to attach this invite to, instead of creating one. */
  linkUserId?: string;
  /** Reuse the login already on this email, after the operator confirmed it. */
  linkExistingAccount?: boolean;
}

export interface InviteUserResponse {
  user: { id: string; auth0Sub: string; email: string };
  reusedAuth0User: boolean;
  linked: boolean;
  /** True when an existing login was reused and no email was sent. */
  linkedExistingAccount: boolean;
  emailSent: boolean;
}

export async function inviteUser(input: InviteUserRequest): Promise<InviteUserResponse> {
  const data = await send<InviteUserRequest, InviteUserResponse>(
    'POST',
    '/api/v1/users/invite',
    input,
  );
  return data.invite as InviteUserResponse;
}

// ── Resend / revoke a pending invite ────────────────────────────────────

interface ResendInviteBody {
  status: 'ok';
  emailSent: boolean;
}

export async function resendInvite(userId: string): Promise<{ emailSent: boolean }> {
  const data = await request<undefined, ResendInviteBody>(
    'POST',
    `/api/v1/users/${encodeURIComponent(userId)}/resend-invite`,
  );
  return { emailSent: data.emailSent };
}

interface RevokeInviteBody {
  status: 'ok';
  /** The row's provisioning status after the invite is revoked. */
  provisioningStatus: ProvisioningStatus;
}

export async function revokeInvite(
  userId: string,
): Promise<{ provisioningStatus: ProvisioningStatus }> {
  const data = await request<undefined, RevokeInviteBody>(
    'POST',
    `/api/v1/users/${encodeURIComponent(userId)}/revoke-invite`,
  );
  return { provisioningStatus: data.provisioningStatus };
}

// ── Detail / edit-role / edit-regions ───────────────────────────────────

export interface UserDetail {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
  /** True iff the user has an Auth0 link. The raw `auth0Sub` is not exposed
   *  on this endpoint (PII-adjacent — see backend service). */
  hasAuth0: boolean;
  roles: RoleKey[];
  regionIds: string[];
  /** Roles the viewer may grant this user. Empty when the viewer can't manage
   *  them or they have no login yet. */
  grantableRoles: RoleKey[];
  /** Top-level regions the viewer may assign. */
  assignableRegionIds: string[];
}

export async function getUserDetail(userId: string): Promise<UserDetail> {
  const data = await send<undefined, UserDetail>(
    'GET',
    `/api/v1/users/${encodeURIComponent(userId)}`,
  );
  return data.user as UserDetail;
}

export interface EditRoleResponse {
  rolesAdded: RoleKey[];
  rolesRemoved: RoleKey[];
  newRoleKeys: RoleKey[];
}

export async function editUserRoles(
  userId: string,
  roleKeys: RoleKey[],
): Promise<EditRoleResponse> {
  const data = await send<{ roleKeys: RoleKey[] }, EditRoleResponse>(
    'PATCH',
    `/api/v1/users/${encodeURIComponent(userId)}/role`,
    { roleKeys },
  );
  return data.result as EditRoleResponse;
}

export interface EditRegionsResponse {
  added: string[];
  removed: string[];
  newRegionIds: string[];
}

export async function editUserRegions(
  userId: string,
  regionIds: string[],
): Promise<EditRegionsResponse> {
  const data = await send<{ regionIds: string[] }, EditRegionsResponse>(
    'PATCH',
    `/api/v1/users/${encodeURIComponent(userId)}/regions`,
    { regionIds },
  );
  return data.result as EditRegionsResponse;
}

export interface EditAccessResponse {
  rolesAdded: RoleKey[];
  rolesRemoved: RoleKey[];
  newRoleKeys: RoleKey[];
  regionsAdded: string[];
  regionsRemoved: string[];
  newRegionIds: string[];
}

/**
 * Change roles and/or regions in ONE request (BON-434).
 *
 * Omit a field to leave that half alone. Sending them together is the point:
 * the server validates the intended end state, so granting a region-scoped
 * role and its first region in one save is legal — as two calls it never was,
 * because the roles call was judged against the not-yet-saved region set.
 */
export async function editUserAccess(
  userId: string,
  changes: { roleKeys?: RoleKey[]; regionIds?: string[] },
): Promise<EditAccessResponse> {
  const data = await send<typeof changes, EditAccessResponse>(
    'PATCH',
    `/api/v1/users/${encodeURIComponent(userId)}/access`,
    changes,
  );
  return data.result as EditAccessResponse;
}

// ── BON-55: deactivate / reactivate / preflight ─────────────────────────

export interface DeactivatePreflight {
  /** Count of upcoming workshops the user is assigned to as staff. */
  upcomingWorkshopCount: number;
  /** App-relative URL the modal links to so the operator can drill into
   *  the user's assignments. Currently points to /staff/:id. */
  deepLinkUrl: string;
}

export async function getDeactivatePreflight(userId: string): Promise<DeactivatePreflight> {
  const data = await send<undefined, DeactivatePreflight>(
    'GET',
    `/api/v1/users/${encodeURIComponent(userId)}/deactivate-preflight`,
  );
  return data.preflight as DeactivatePreflight;
}

export async function deactivateUser(userId: string): Promise<void> {
  await send<undefined, { cacheInvalidated: boolean }>(
    'PATCH',
    `/api/v1/users/${encodeURIComponent(userId)}/deactivate`,
  );
}

export async function reactivateUser(userId: string): Promise<void> {
  await send<undefined, { reactivatedUserId: string }>(
    'PATCH',
    `/api/v1/users/${encodeURIComponent(userId)}/reactivate`,
  );
}
